import { z } from 'zod';

/**
 * The TRADE somebody practises — "Master Cutter", "Embroiderer", "Finishing Tailor" — as a list
 * each shop writes for itself.
 *
 * Read the next paragraph before touching anything in this file.
 *
 * A craft role is a LABEL. It is not a role in the authorization sense and must never become
 * one. The auth role (`owner` / `manager` / `tailor`, in `constants/roles.ts`) decides what a
 * person may do; the permission matrix reads that and nothing else. Nothing in this module may
 * be read by a guard, a middleware, a route or a service to decide whether an action is
 * allowed, and a craft role must never be derived from — or used to infer — an auth role. The
 * two happen to be called "role" in English and are otherwise unrelated: one shop's
 * "Assistant Manager" craft role can sit on a person whose auth role is `tailor`, and that is
 * a perfectly ordinary shop rather than a mistake to reconcile.
 *
 * It is also NOT `specialisations`. Those name the payable stages a person normally handles and
 * are what the assignment screens read as a hint. A craft role is what the person is called on
 * the shop floor, and no assignment, suggestion or ordering anywhere reads it.
 *
 * The storage rules follow `custom-fields.ts`, for the same reason: a KEY is derived once from the
 * label the shop first typed and is then immutable, so a role renamed after two years still
 * matches the staff records pointing at it. Renaming writes `label`. Retiring writes
 * `active: false` — the entry then leaves every picker and filter but still resolves a label for
 * the people who already carry it.
 */

export const MAX_CRAFT_ROLE_LABEL_LENGTH = 40;
/** A cap, not a target. Past this a picker is a scroll rather than a choice. */
export const MAX_CRAFT_ROLES = 40;

export const craftRoleKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-zA-Z0-9_]*$/, 'A craft role key is lower snake_case');

export const craftRoleLabelSchema = z
  .string()
  .trim()
  .min(1, 'Give the craft role a name')
  .max(MAX_CRAFT_ROLE_LABEL_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/**
 * One row of a shop's list, both as stored and as served.
 *
 * There is no separate "resolved" shape the way custom fields have one, because there is nothing
 * to resolve: a craft role has no product-owned default behind it. The shop's label IS the name
 * of it.
 */
export const craftRoleSchema = z.object({
  key: craftRoleKeySchema,
  label: z.string(),
  /** False for a trade the shop no longer hires for. Hidden from pickers, still resolvable. */
  active: z.boolean(),
  order: z.number().int(),
});
export type CraftRole = z.infer<typeof craftRoleSchema>;

export const craftRoleListSchema = z.object({
  roles: z.array(craftRoleSchema),
});
export type CraftRoleList = z.infer<typeof craftRoleListSchema>;

/**
 * What every shop starts with, and what a shop whose settings predate this feature reads as.
 *
 * The keys are the slugs the labels produce, written out rather than derived so this list is
 * the same for a shop seeded at signup and a shop that never stored a row — which is what makes
 * the feature safe to ship with no backfill.
 *
 * `tailor` appearing here is a coincidence of English, not a link to the auth role of the same
 * name. Nothing reads one as the other; see the note at the top of this file.
 */
export const DEFAULT_CRAFT_ROLES: readonly { key: string; label: string }[] = Object.freeze([
  { key: 'master_cutter', label: 'Master Cutter' },
  { key: 'tailor', label: 'Tailor' },
  { key: 'embroiderer', label: 'Embroiderer' },
  { key: 'machine_embroidery', label: 'Machine Embroidery' },
  { key: 'finishing_tailor', label: 'Finishing Tailor' },
  { key: 'designer', label: 'Designer' },
  { key: 'assistant_manager', label: 'Assistant Manager' },
]);

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

/**
 * Adding one. The KEY is absent on purpose — it is derived from the label, once, by the server,
 * exactly as a custom field's is.
 */
export const addCraftRoleSchema = z.object({
  label: craftRoleLabelSchema,
});
export type AddCraftRoleInput = z.input<typeof addCraftRoleSchema>;

/** Editing one. `key` is not accepted, so a rename can only ever be a label. */
export const updateCraftRoleSchema = z
  .object({
    label: craftRoleLabelSchema.optional(),
    active: z.boolean().optional(),
  })
  .refine((patch) => patch.label !== undefined || patch.active !== undefined, {
    message: 'Nothing to update',
  });
export type UpdateCraftRoleInput = z.input<typeof updateCraftRoleSchema>;

/** A complete running order. Keys the caller omits keep their relative order and follow. */
export const reorderCraftRolesSchema = z.object({
  order: z.array(craftRoleKeySchema).min(1),
});
export type ReorderCraftRolesInput = z.input<typeof reorderCraftRolesSchema>;
